import without from 'lodash/without';
import { useQueryParams } from 'modules/routing/useQueryParams';
import * as yup from 'yup';

const yupStringArray = yup
  .array()
  .of(yup.string() as yup.StringSchema<string>)
  .required()
  .default([] as string[]);

const yupNumberArray = yup
  .array()
  .of(yup.number() as yup.NumberSchema<number>)
  .required()
  .default([] as number[]);

export enum MarketplaceSortOptions {
  MostPopular = '-totalFollowers',
  HighestReturns = '-last_30DaysReturns',
  Last30DaysFollowersPnL = '-totalPnlCopiers',
  Oldest = 'createdAt',
  Newest = '-createdAt',
}

export const strategiesStateSchema = {
  market: yupStringArray,
  traderId: yupStringArray,
  symbolId: yupNumberArray,
  strategyTypeId: yupStringArray,
  exchangeId: yupNumberArray,
  sort: yup.string().default(MarketplaceSortOptions.Last30DaysFollowersPnL),
};

export type StrategiesState = yup.TypeFromShape<
  typeof strategiesStateSchema,
  unknown
>;

export default function useStrategiesState() {
  const [filtersState, setFilters] = useQueryParams(strategiesStateSchema);

  const updateFilters = (update: Partial<StrategiesState>) => {
    setFilters({ ...filtersState, ...update });
  };

  const registerFilter = <T extends keyof StrategiesState>(name: T) => {
    return {
      value: filtersState[name],
      onChange: (value: StrategiesState[T]) => updateFilters({ [name]: value }),
    };
  };
  const registerTag = <T extends keyof StrategiesState>(
    name: T,
    value: StrategiesState[T] extends any[] ? StrategiesState[T][number] : never
  ) => {
    return {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      key: `${name}-${value}`,
      value,
      onRemove: () =>
        updateFilters({
          [name]: without(filtersState[name] as any, value),
        }),
    };
  };

  function registerSort() {
    return {
      value: filtersState.sort,
      onChange: (sort: string) =>
        updateFilters({
          sort,
        }),
    };
  }

  return {
    filtersState,
    setFilters,
    updateFilters,
    registerFilter,
    registerTag,
    registerSort,
  };
}
